import { Box, OutlinedInput, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { sxDarkGreyBody, sxLabel, sxLabeledOutlinedInput, sxTitle } from '../../../assets/styles/CommonSxProps';
import AudioLoadingDescription from '../../../components/Accessibility/AudioLoadingDescription';
import CircularProgressLoading from '../../../components/Accessibility/CircularProgressLoading';
import LoadingApp from '../../../components/LoadingApp';
import ModalFeedBack from '../../../components/ModalFeedBack';
import { AnexoRecebido } from '../../../interfaces/entities/Anexo/AnexoRecebido';
import { getAnexoProntu, getListaAnexoProntuario } from '../../../services/AnexosProntuService';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { base64toBlob } from '../../../utils/Base64ToBlob';
import AnexosTable from './AnexosTable';
import { sxInput } from './sxProps';
import PageTitle from '../../../components/Accessibility/PageTitle';

const Anexos: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingPdf, setLoadingPdf] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const [listaAnexo, setListaAnexo] = useState<AnexoRecebido[]>([]);
  const [listaAnexoFilter, setListaAnexoFilter] = useState<AnexoRecebido[]>([]);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);

  const accessibility = useAccessibility();

  const fetchListaAnexoProntuario = async () => {
    setLoading(true);
    const response = await getListaAnexoProntuario();
    if (response?.status === 200) {
      setListaAnexo(response.data);
      setListaAnexoFilter(response.data);
    } else {
      console.error(response);
      openModalError('Erro ao consultar lista de anexos', 'Ocorreu um erro ao consultar os anexos. Tente novamente em instantes.');
    }
    setLoading(false);
  }

  const fetchAnexoProntuario = async (idAnexoProntu: string) => {
    setLoadingPdf(true);
    const response = await getAnexoProntu(idAnexoProntu);
    if (response?.status === 200) {
      abrirPdf(response.data.anexoBase64);
    } else {
      console.error(response);
      openModalError('Erro ao consultar arquivo', 'Ocorreu um erro ao consultar o anexo referido. Tente novamente em instantes.');
    }
    setLoadingPdf(false);
  }

  useEffect(() => {
    fetchListaAnexoProntuario();
  }, []);

  useEffect(() => {
    applyFilter(filter);
  }, [filter]);

  const applyFilter = (value: string) => {
    if (!value) {
      setListaAnexoFilter(listaAnexo);
    } else {
      const filtered = listaAnexo.filter(item =>
        item.dtHoraInclusao.includes(value) || item.nomePrestador.includes(value)
      );
      setListaAnexoFilter(filtered);
    }
  };

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: 'error',
      title: title,
      subTitle: subtitle,
      textButton: 'Fechar',
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null)
    });
  }

  const openAnexo = (idAnexo: string) => {
    fetchAnexoProntuario(idAnexo);
  }

  const abrirPdf = (pdf: string) => {
    const pdfBase64 = pdf.split(',')[1];
    const blob = base64toBlob(pdfBase64);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl);
  }

  return (
    <>
      <div className='feature-container'>
        <AudioLoadingDescription loading={loading} />
        <LoadingApp showLoading={loadingPdf} text={'Gerando arquivo...'} />
        <PageTitle text="Meus documentos" />
        <Box sx={sxLabeledOutlinedInput}>
          <Typography style={accessibility.textStyleAccessibility} sx={sxLabel}>Pesquisar data ou nome do(a) solicitante</Typography>
          <label htmlFor='textInput' aria-label='pesquisar data ou nome do solicitante' />
          <OutlinedInput
            id='textInput'
            type={'text'}
            value={filter}
            style={accessibility.textStyleAccessibility}
            placeholder='Exemplos: 05/04/2024 (data) ou José (nome)'
            onChange={(e) => {
              const value = e.target.value;
              setFilter(value.toUpperCase());
            }}
            name='filter'
            maxRows={1}
            sx={sxInput}
          />
        </Box>
        {loading ? <CircularProgressLoading /> :
          <>
            {listaAnexoFilter.length ? <AnexosTable data={listaAnexoFilter} openAnexo={openAnexo} /> : <Typography sx={sxDarkGreyBody} style={accessibility.textStyleAccessibility}>Nenhum anexo encontrado.</Typography>}
          </>}
      </div>
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
    </>
  )
}

export default Anexos;